import type { Address, MarketId } from '@morpho-org/blue-sdk'
import type { ApiInputToken, Rewards } from '@morpho-org/dapp-core'
import type { InferContractRouterOutputs } from '@orpc/contract'

import type { UnifiedVaultWarning } from '@/helpers/warnings'
import type { Contract } from '@/lib/orpc/contracts'

import { computeRewards } from '@/helpers/rewards'
import { sortingOnKey } from '@/helpers/sorting'
import { WarningLevel } from '@/types/enums'

export type VaultV1ActionPanelOutput =
  InferContractRouterOutputs<Contract>['vaultV1']['actionPanel']

interface VaultV1ActionPanelAllocation {
  supplyAssetsUsd: number | null
  market: {
    marketId: MarketId
    collateralAsset: ApiInputToken | null
    loanAsset: ApiInputToken
  }
}

/** What the V1 action panel, its dialogs and the recap read, in the shape their hooks take. */
export interface VaultV1ActionPanelData {
  address: Address
  name: string
  image: string | null
  featured: boolean
  asset: ApiInputToken
  warnings: UnifiedVaultWarning[]
  rewards: Rewards[]
  state: {
    fee: number
    netApy: number | null
    netApyExcludingRewards: number | null
    totalAssetsUsd: number | null
    curators: { id: string; name: string; image: string | null }[]
    allocation: VaultV1ActionPanelAllocation[]
  }
}

const WARNING_LEVELS: Readonly<Record<string, WarningLevel>> = {
  red: WarningLevel.Red,
  yellow: WarningLevel.Yellow
}

/** The bridge writes levels in lowercase; the panel's gates compare against `WarningLevel`. */
function toWarnings(warnings: VaultV1ActionPanelOutput['warnings']): UnifiedVaultWarning[] {
  return warnings.flatMap(({ type, level }) => {
    const known = WARNING_LEVELS[level.toLowerCase()]
    return known ? [{ type, level: known }] : []
  })
}

/**
 * Maps the `vaultV1.actionPanel` procedure to the panel's view model. Null when the vault asset
 * has no decimals: amounts cannot be read or entered then, so callers treat it as missing data.
 */
export function toVaultV1ActionPanelData(
  output: VaultV1ActionPanelOutput,
  chainId: number
): VaultV1ActionPanelData | null {
  const { asset } = output
  if (asset.decimals === null) return null

  const chain = { id: chainId }
  const vaultAsset: ApiInputToken = {
    address: asset.address,
    chain,
    name: asset.name ?? '',
    symbol: asset.symbol ?? '',
    decimals: asset.decimals,
    logoURI: asset.logoUri,
    priceUsd: asset.priceUsd,
    yield: asset.yieldApy === null ? null : { apy: asset.yieldApy }
  }

  const rewards = sortingOnKey(
    'apr',
    output.rewards.map(reward =>
      computeRewards({
        rewardToken: {
          address: reward.token.address,
          chain: { id: reward.token.chainId },
          symbol: reward.token.symbol ?? '',
          name: reward.token.symbol ?? '',
          // Only the symbol and logo render; the bridge reward token carries no decimals.
          decimals: 18,
          logoURI: reward.token.logoUri
        },
        apr: reward.supplyApr
      })
    )
  ).filter(reward => reward.apr && reward.apr > 0n)

  return {
    address: output.address,
    name: output.name,
    image: output.image,
    featured: output.featured,
    asset: vaultAsset,
    warnings: toWarnings(output.warnings),
    rewards,
    state: {
      fee: output.performanceFee,
      netApy: output.netApy,
      netApyExcludingRewards: output.netApyExcludingRewards,
      totalAssetsUsd: output.totalAssetsUsd,
      curators: output.curators.map(({ id, name, image }) => ({ id, name, image })),
      allocation: output.allocations.map(({ market, supplyAssetsUsd }) => ({
        supplyAssetsUsd,
        market: {
          marketId: market.marketId as MarketId,
          collateralAsset: market.collateralToken
            ? {
                address: market.collateralToken.address,
                chain,
                symbol: market.collateralToken.symbol ?? '',
                name: market.collateralToken.symbol ?? '',
                decimals: market.collateralToken.decimals ?? 18,
                logoURI: market.collateralToken.logoUri
              }
            : null,
          loanAsset: vaultAsset
        }
      }))
    }
  }
}
